import { EKycFlowType, IKycFlowDto } from "../types/kyc/kycFlowTypes"
import { EPayFacType } from "../types/payment/EPayFacType"
import { hasValue } from "../utils/arrayUtil"
import { ETermsType } from "./termsService"

export function canEditKycFlow(flow: IKycFlowDto | undefined) {
    return (
        flow?.subState === "ChangesRequiredForPartial" ||
        flow?.subState === "ChangesRequiredForComplete" ||
        ((flow?.state === "Partial" || flow?.state === "Initiated") && flow?.subState === "None")
    )
}

export function getActivePayFac(countryCode: string): EPayFacType {
    if (["NO", "SE", "DK"].includes(countryCode)) {
        return "Clearhaus"
    }
    return "Digiteal"
}

export function getTermsForPayFacs(payFacTypes: EPayFacType[]): ETermsType[] {
    return payFacTypes.map((t) => payFacTypeToTermsType[t]).flat()
}

export function getPayFacType(countryCode: string, payFacTypes: EPayFacType[]): EPayFacType | undefined {
    if (payFacTypes.length === 0) {
        return undefined
    }
    const defaultPayFac = getActivePayFac(countryCode)
    return payFacTypes.includes(defaultPayFac) ? defaultPayFac : payFacTypes[0]
}

// First is default flow
const payFacTypeToKycFlowType: { [payFacType in EPayFacType]: EKycFlowType[] | undefined } = {
    Clearhaus: ["ClearhausApi"],
    Shift4: undefined,
    Digiteal: ["DigitealApi"],
}

const payFacTypeToTermsType: { [payFacType in EPayFacType]: ETermsType[] } = {
    Clearhaus: ["TermsAcquiringClearhaus"],
    Shift4: [],
    Digiteal: ["TermsPayFaqDigiteal"],
}

export function isKycFlowsOnboardedForPayFac(
    organizationId: string | undefined, // undefined means flows already filtered by organizationId
    webshopIds: string[],
    flows: IKycFlowDto[],
    payFacType: EPayFacType,
    configs?: {
        includeAwaitingConfirmation?: boolean
        includeAwaitingTransfer?: boolean
    }
) {
    const kycFlowTypes = payFacTypeToKycFlowType[payFacType]
    const bestFlows = (kycFlowTypes ?? []).map((t) => getBestKycFlow(flows, organizationId, webshopIds, t)).filter(hasValue)
    return !!bestFlows.find(
        (f) =>
            f.state === "Complete" ||
            f.state === "Partial" ||
            (configs?.includeAwaitingConfirmation &&
                f.state !== "Abandoned" &&
                (f.subState === "AwaitingConfirmationForPartial" || f.subState === "AwaitingConfirmationForComplete")) ||
            (configs?.includeAwaitingTransfer &&
                f.state !== "Abandoned" &&
                (f.subState === "AwaitingCompanyTransferForPartial" || f.subState === "AwaitingCompanyTransferForComplete"))
    )
}

export function getBestKycFlow(
    flows: IKycFlowDto[],
    organizationId: string | undefined, // undefined means flows already filtered by organizationId
    webshopIds: string[],
    type: EKycFlowType
) {
    const potentialFlows = flows.filter((f) => (!organizationId || f.organizationId === organizationId) && f.flowType === type)
    const exactMatches = potentialFlows.filter((f) => f.webshopId && webshopIds.includes(f.webshopId))
    if (exactMatches.length > 0) {
        return exactMatches[0]
    }
    const organizationMatches = potentialFlows.filter((f) => !f.webshopId)
    if (organizationMatches.length > 0) {
        return organizationMatches[0]
    }
    return undefined
}
